import { Injectable } from '@angular/core';
import { User } from '@microsoft/microsoft-graph-types-beta';
import { BehaviorSubject, combineLatest, filter, map, take } from 'rxjs';
import {
    Baseline,
    BaselineDeviation,
    BaselinePostureCountsByDate,
    CombinedDeviation,
    CurrentPostureCount,
    Header,
    TicketCount,
    TopAlert,
} from '../interfaces/ng-oct-report.interface';
import {
    getPostureControlsInThisPeriod,
    GroupBaselineDeviationWithTimelineElementsByBaseline,
    findUserNameById,
} from '../utils';

@Injectable({
    providedIn: 'root',
})
export class NgOctReportService {
    public header$ = new BehaviorSubject<Header | null>(null);
    public graphUsers$ = new BehaviorSubject<User[] | null>(null);
    public topAlerts$ = new BehaviorSubject<TopAlert[] | null>(null);
    public ticketCount$ = new BehaviorSubject<TicketCount[] | null>(null);
    /**
     * @param  {Baseline[]} baselines$ Obserservable of an array of baselines from the client tenant
     */

    public baselines$ = new BehaviorSubject<Baseline[] | null>(null);
    /**
     * Existing Tenant Baselines which never have any deviations/resolutions after creation
     */
    public tenantCleanBaselines$ = new BehaviorSubject<BaselineDeviation[] | null>(null);
    /**
     * Existing Group Baselines which never have any deviations/resolutions after creation
     */
    public groupCleanBaselines$ = new BehaviorSubject<BaselineDeviation[] | null>(null);
    /**
     *  @param tenant_count -  @param has_deviated the number of tenant baselines that are currently deviating @param not_deviated the number of tenant baselines that are currently passing
     *  @param group_count -  @param has_deviated the number of group baselines that are currently deviating @param has_deviated the number of group baselines that are currently passing
     */
    public allBaselines_posture_count$ = new BehaviorSubject<{
        tenant_count: CurrentPostureCount;
        group_count: CurrentPostureCount;
    } | null>(null);
    /**
     * @param {BaselinePostureCountsByDate} tenant_baselines_posture_controls_in_this_period - an Object list with tenant baselines' posture control counts (deviating, compliance, monitoring) by date in the selected period
     */
    public tenant_baselines_posture_controls_in_this_period$ = new BehaviorSubject<BaselinePostureCountsByDate | null>(
        null,
    );
    /**
     * @param {BaselinePostureCountsByDate} group_baselines_posture_controls_in_this_period - an Object list with group baselines' posture control counts (deviating, compliance, monitoring) by date in the selected period
     */
    public group_baselines_posture_controls_in_this_period$ = new BehaviorSubject<BaselinePostureCountsByDate | null>(
        null,
    );
    /**
     *  baseline_deviations$ - an array of tenant baselines, contains a timeline of changes (created, deviation, remediation) on each baseline within the selected period
     */
    public tenantBaselineDeviationsByBaseline$ = new BehaviorSubject<BaselineDeviation[] | null>(null);
    /**
     *  baseline_deviations$ - an array of group baselines, contains a timeline of changes (created, deviation, remediation) on each baseline within the selected period
     */
    public groupBaselineDeviationsByBaseline$ = new BehaviorSubject<BaselineDeviation[] | null>(null);
    /**
     * set the header information for the report
     * @param {Header} header - the object contains, @date start & end date of the selected period and @tenant the tenant name
     */
    public setHeader(header: Header): void {
        this.header$.next(header);
    }

    /**
     * set the users array fetched from graph API
     * @param {User[]} users - an array of graph users
     */
    public setGraphUsers(users: User[]) {
        this.graphUsers$.next(users);
    }

    public setBaselines(baselines: Baseline[]) {
        this.baselines$.next(baselines);
    }

    /**
     *  set the counts of tenant and group baselines that currently deviating and passing
     *  @param tenant_count -  @param has_deviated the number of tenant baselines that are currently deviating @param not_deviated the number of tenant baselines that are currently passing
     *  @param group_count -  @param has_deviated the number of group baselines that are currently deviating @param has_deviated the number of group baselines that are currently passing
     */
    public setAllBaselineCurrentPostureCount(counts: {
        tenant_count: CurrentPostureCount;
        group_count: CurrentPostureCount;
    }) {
        this.allBaselines_posture_count$.next(counts);
    }

    /**
     * set the array of alerts that occurred in the selected period
     * @param {TopAlert[]} alerts - the array of Alert Items
     */
    public createTopAlerts(alerts: TopAlert[]) {
        this.topAlerts$.next(alerts);
    }

    public getTicketCount(ticket_counts: TicketCount[]) {
        this.ticketCount$.next(ticket_counts);
    }

    /**
     * pass in the raw baseline deviations array within the selected period from sway, and transform data to topBaselines$ and Baselines$
     * @output top_baseline_deviations$ - baselines that are still deviating in the end of the selected period
     * @output baseline_deviations$ - (Security Control) an array of baselines, contains a timeline of changes (created, deviation, remediation) on each baseline
     * @param {CombinedDeviation[]} baseline_deviations - the array of combined baseline deviations within the selected period
     * @todo need to add category for tenant baseline : External (Guest) Users Resharing, we hardcode its category and type here for now
     */
    public getBaselineDeviations(baseline_deviations: CombinedDeviation[]) {
        combineLatest([
            this.header$.pipe(
                filter((header) => header !== null),
                map((header) => header as Header),
                take(1),
            ),
            this.graphUsers$.pipe(
                filter((users) => users !== null),
                map((users) => users as User),
                take(1),
            ),
            this.baselines$.pipe(
                filter((baselines) => baselines !== null),
                map((baselines) => baselines as Baseline[]),
                take(1),
            ),
        ]).subscribe(([header, users, baselines]) => {
            const start = header.date.start;
            const end = header.date.end;

            console.log(header, users, start, end);

            try {
                // TODO: we shouldn't get all deviations from DB as it will be a massive volume

                /**** TENANT ****/

                // 1. Set the Tenant Baseline Deviations In Time Range (tenantBaselineDeviationsInTimeRange)
                // tenant deviations (deviation in timerange, w/o resolved in timerange)
                const tenantBaselineDeviationsInTimeRange = this.GetBaselineDeviationsInTimeRange(
                    baseline_deviations,
                    'tenant',
                    start,
                    end,
                );

                const tenantBaselines: Baseline[] = this.GetCurrentExistingBaselinesByType(baselines, 'tenant'); // existing baselines

                const formattedTenantBaselineDeviations = GroupBaselineDeviationWithTimelineElementsByBaseline(
                    tenantBaselineDeviationsInTimeRange,
                    users as User[],
                ); // formatted tenant baseline deviations with timeline elements per baseline
                this.tenantBaselineDeviationsByBaseline$.next(formattedTenantBaselineDeviations);

                // 2. Get and Set the Clean Tenant Baselines (tenantCleanBaselines)
                // Deviating / Resolved Baselines within Time Range
                const tenantBaselineIdsWithDeviationInTimeRange: Set<string> = new Set(
                    tenantBaselineDeviationsInTimeRange.map((obj) => obj.baseline_id),
                ); // Filter unique baseline_id values

                const deviatingTenantBaselineIdsBeforeTimeRange: Set<string> = this.GetDeviatedBaselinesBeforeTimeRange(
                    baseline_deviations,
                    tenantBaselineIdsWithDeviationInTimeRange,
                    'tenant',
                    start,
                ); // Unhealthy Baselines before Time Range

                const tenantCleanBaselines: Baseline[] = this.filterCleanBaselines(
                    tenantBaselines,
                    tenantBaselineIdsWithDeviationInTimeRange,
                    deviatingTenantBaselineIdsBeforeTimeRange,
                ); // Clean tenant baselines (no deviation nor resolution happened)
                const formattedTenantCleanBaselines: BaselineDeviation[] = tenantCleanBaselines.map((item) => ({
                    ...item,
                    timelineElements: [],
                    status: 'pass',
                }));
                this.tenantCleanBaselines$.next(formattedTenantCleanBaselines);

                // 3. Find the Tenant Baselines that has no deviation in time range but has deviatiion before time range,
                //    then set the Baseline Deviations for these baselines (tenantBaselineDeviationsBeforeTimeRange)
                const tenantBaselineDeviationsBeforeTimeRange = this.GetBaselineDeviationsBeforeTimeRange(
                    baseline_deviations,
                    deviatingTenantBaselineIdsBeforeTimeRange,
                    'tenant',
                    start,
                );
                const formattedTenantBaselineDeviationsBeforeTimeRange = tenantBaselineDeviationsBeforeTimeRange.reduce(
                    (acc: BaselineDeviation[], item) => {
                        const formatted_item: BaselineDeviation = {
                            category: item.category,
                            type: 'tenant',
                            name: item.name,
                            baseline_name: item.baseline_name,
                            baseline_id: item.baseline_id,
                            timelineElements: [],
                            status: 'deviated',
                        };
                        acc.push(formatted_item);
                        return acc;
                    },
                    [] as BaselineDeviation[],
                );
                const tenantBaselineDeviationsByBaseline = [
                    ...formattedTenantBaselineDeviations,
                    ...formattedTenantBaselineDeviationsBeforeTimeRange,
                    ...formattedTenantCleanBaselines,
                ];
                console.log('tenantBaselineDeviationsByBaseline', tenantBaselineDeviationsByBaseline);
                this.tenantBaselineDeviationsByBaseline$.next(tenantBaselineDeviationsByBaseline);

                /**** GROUP ****/
                // 1. Set the Group Baseline Deviations In Time Range (groupBaselineDeviationsInTimeRange)
                // group baseline deviations (deviation in timerange, w/o resolved in timerange)
                const groupBaselineDeviationsInTimeRange = this.GetBaselineDeviationsInTimeRange(
                    baseline_deviations,
                    'group',
                    start,
                    end,
                );

                const groupBaselines: Baseline[] = this.GetCurrentExistingBaselinesByType(baselines, 'group'); // existing baselines

                const formattedGroupBaselineDeviations = GroupBaselineDeviationWithTimelineElementsByBaseline(
                    groupBaselineDeviationsInTimeRange,
                    users as User[],
                );
                console.log('formattedGroupBaselineDeviations', formattedGroupBaselineDeviations);
                this.groupBaselineDeviationsByBaseline$.next(formattedGroupBaselineDeviations);

                // 2. Get and Set the Clean Tenant Baselines (tenantCleanBaselines)
                // Deviating / Resolved Baselines within Time Range
                const groupBaselineIdsWithDeviationInTimeRange: Set<string> = new Set(
                    groupBaselineDeviationsInTimeRange.map((obj) => obj.baseline_id),
                ); // Filter unique baseline_id values

                const deviatingGroupBaselineIdsBeforeTimeRange: Set<string> = this.GetDeviatedBaselinesBeforeTimeRange(
                    baseline_deviations,
                    groupBaselineIdsWithDeviationInTimeRange,
                    'group',
                    start,
                ); // Unhealthy Baselines before Time Range

                const groupCleanBaselines: Baseline[] = this.filterCleanBaselines(
                    groupBaselines,
                    groupBaselineIdsWithDeviationInTimeRange,
                    deviatingGroupBaselineIdsBeforeTimeRange,
                ); // Clean tenant baselines (no deviation nor resolution happened)
                const formattedGroupCleanBaselines: BaselineDeviation[] = groupCleanBaselines.map((item) => ({
                    ...item,
                    timelineElements: [],
                    status: 'pass',
                }));
                this.groupCleanBaselines$.next(formattedGroupCleanBaselines);

                // 3. Find the Group Baselines that has no deviation in time range but has deviation before time range,
                //    then set the Baseline Deviations for these baselines (groupBaselineDeviationsBeforeTimeRange)
                const groupBaselineDeviationsBeforeTimeRange = this.GetBaselineDeviationsBeforeTimeRange(
                    baseline_deviations,
                    deviatingGroupBaselineIdsBeforeTimeRange,
                    'group',
                    start,
                );
                const formattedGroupBaselineDeviationsBeforeTimeRange = groupBaselineDeviationsBeforeTimeRange.reduce(
                    (acc: BaselineDeviation[], item) => {
                        const formatted_item: BaselineDeviation = {
                            category: item.category,
                            type: 'group',
                            group_name: item.group_name,
                            user_id: item.user_id,
                            user_name: findUserNameById(item.user_id, users as User[]),
                            name: item.name,
                            baseline_name: item.baseline_name,
                            baseline_id: item.baseline_id,
                            timelineElements: [],
                            status: 'deviated',
                        };
                        acc.push(formatted_item);
                        return acc;
                    },
                    [] as BaselineDeviation[],
                );
                const groupBaselineDeviationsByBaseline = [
                    ...formattedGroupBaselineDeviations,
                    ...formattedGroupBaselineDeviationsBeforeTimeRange,
                    ...formattedGroupCleanBaselines,
                ];
                console.log('gropuBaselineDeviationsByBaseline', groupBaselineDeviationsByBaseline);
                this.groupBaselineDeviationsByBaseline$.next(groupBaselineDeviationsByBaseline);

                /**** Posture Controls Charts ****/
                const tenant_baselines_posture_controls_in_this_period = getPostureControlsInThisPeriod(
                    baseline_deviations,
                    baselines,
                    'tenant',
                    start,
                    end,
                );
                this.tenant_baselines_posture_controls_in_this_period$.next(
                    tenant_baselines_posture_controls_in_this_period,
                );

                const group_baselines_posture_control_in_this_period = getPostureControlsInThisPeriod(
                    baseline_deviations,
                    baselines,
                    'group',
                    start,
                    end,
                );
                this.group_baselines_posture_controls_in_this_period$.next(
                    group_baselines_posture_control_in_this_period,
                );

                console.log('finish posture control chart format');
            } catch (e: any) {
                console.log('ng-oct-report-error:', e);
            }
        });
    }

    private GetCurrentExistingBaselinesByType(baselines: Baseline[], type: string): Baseline[] {
        return baselines.filter((b) => b.type === type);
    }

    private GetBaselineDeviationsInTimeRange(
        baseline_deviations: CombinedDeviation[],
        type: string,
        start: string,
        end: string,
    ) {
        return baseline_deviations.filter(
            (bd) =>
                bd.type === type &&
                ((bd.deviation_detect_time >= start && bd.deviation_detect_time <= end) ||
                    (bd.deviation_resolve_time !== null &&
                        bd.deviation_resolve_time >= start &&
                        bd.deviation_resolve_time <= end) ||
                    (bd.baseline_created >= start && bd.baseline_created <= end)),
        );
    }

    private GetBaselineDeviationsBeforeTimeRange(
        baselineDeviations: CombinedDeviation[],
        includedBaselineIds: Set<string>,
        type: string,
        start: string,
    ): CombinedDeviation[] {
        const result: CombinedDeviation[] = [];
        for (const deviation of baselineDeviations) {
            if (
                includedBaselineIds.has(deviation.baseline_id) &&
                deviation.type === type &&
                deviation.deviation_detect_time < start &&
                deviation.deviation_resolve_time === null
            ) {
                result.push(deviation);
            }
        }
        return result;
    }

    private GetDeviatedBaselinesBeforeTimeRange(
        baselineDeviations: CombinedDeviation[],
        baselineIdsExcluded: Set<string>,
        type: string,
        start: string,
    ): Set<string> {
        const result: Set<string> = new Set();
        for (const deviation of baselineDeviations) {
            if (
                !baselineIdsExcluded.has(deviation.baseline_id) &&
                deviation.type === type &&
                deviation.deviation_resolve_time === null &&
                deviation.deviation_detect_time < start
            ) {
                result.add(deviation.baseline_id);
            }
        }
        return result;
    }

    private filterCleanBaselines(baselines: Baseline[], excludedIds1: Set<string>, excludedIds2: Set<string>) {
        const excludedIds = new Set([...excludedIds1, ...excludedIds2]);
        const filtered_baselines = baselines.filter((baseline) => !excludedIds.has(baseline.id));
        return filtered_baselines;
    }

    constructor() {}
}
